import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Star, Check, WarningCircle, XCircle, HandCoins, ArrowUpRight, ClockCountdown, Truck, Copy,
} from "@phosphor-icons/react";
import { api, formatErr, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FoundingHat } from "@/components/FoundingHat";

// UI copy + accent colour per lifecycle status.
const STATUS_UI = {
  pending_seller:    { label: "Awaiting seller validation", cls: "" },
  pending_buyer:     { label: "Awaiting your payment",      cls: "h57-chip-gold" },
  pending_shipment:  { label: "Awaiting shipment",          cls: "h57-chip-orange" },
  processing:        { label: "In transit · Processing",    cls: "h57-chip-orange" },
  pending_feedback:  { label: "Awaiting feedback",          cls: "h57-chip-orange" },
  completed:         { label: "Completed",                  cls: "h57-chip-gold" },
  declined:          { label: "Declined",                   cls: "!border-red-500/50 !text-red-400" },
  expired:           { label: "Expired",                    cls: "!border-red-500/50 !text-red-400" },
  cancelled:         { label: "Cancelled",                  cls: "" },
  disputed:          { label: "Disputed",                   cls: "!border-red-500/60 !text-red-400" },
};

// Which status labels apply to *me* as which party. Used to pick the CTA.
const cta = {
  seller: {
    pending_seller:    ["validate", "decline", "cancel"],
    pending_buyer:     ["cancel", "dispute"],
    pending_shipment:  ["mark-shipped", "dispute"],
    processing:        ["mark-shipped", "complete", "dispute"],
    pending_feedback:  ["feedback"],
  },
  buyer: {
    pending_seller:    ["cancel"],
    pending_buyer:     ["mark-paid", "cancel", "dispute"],
    pending_shipment:  ["dispute"],
    processing:        ["dispute"],
    pending_feedback:  ["feedback"],
  },
};

function StarPicker({ value, onChange, size = 20 }) {
  return (
    <div className="flex items-center gap-1" data-testid="feedback-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" data-testid={`feedback-star-${n}`}
          onClick={() => onChange(n)}
          className="transition-colors">
          <Star size={size} weight={n <= value ? "fill" : "regular"}
            className={n <= value ? "text-[#FFD700]" : "text-zinc-600 hover:text-zinc-400"} />
        </button>
      ))}
    </div>
  );
}

function CountdownChip({ expiresAt }) {
  const remaining = useMemo(() => {
    if (!expiresAt) return null;
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms <= 0) return "expired";
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    if (h > 0) return `${h}h ${m}m left`;
    return `${m}m left`;
  }, [expiresAt]);
  if (!remaining) return null;
  return (
    <span className={`h57-chip !py-1 !px-2 inline-flex items-center gap-1 text-[10px] ${remaining === "expired" ? "!border-red-500/60 !text-red-400" : ""}`}>
      <ClockCountdown size={11} weight="bold" /> {remaining}
    </span>
  );
}

// Normalize a seller-supplied payment URL/handle into one of:
//   { kind: 'url',   href: 'https://...' }  — safe absolute link
//   { kind: 'email', email: 'foo@bar.com' } — PayPal only; no one-click URL exists,
//                                             UI falls back to a "copy email" panel
//   null                                    — unsafe / empty / unrecognizable
// Rationale: sellers frequently paste unusual formats — bare handles, @user,
// bare domains, or their raw PayPal *email* (which cannot be turned into a
// one-click link and would resolve to `https://foo@bar.com` = hotmail.com).
function normalizePayLink(raw, provider) {
  if (!raw || typeof raw !== "string") return null;
  const s = raw.trim();
  if (!s) return null;
  // Already absolute — trust it.
  if (/^https?:\/\//i.test(s)) return { kind: "url", href: s };
  // Email → only paypal can meaningfully use it (and only as a copy target).
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (emailRe.test(s)) {
    if (provider === "paypal") return { kind: "email", email: s };
    return null; // venmo/cashapp/cc don't work with a bare email
  }
  // Handle-style: @user or plain "user" (no slash, no dot).
  const isHandle = s.startsWith("@") || (!s.includes("/") && !s.includes("."));
  if (isHandle) {
    const handle = s.replace(/^@/, "");
    if (!handle) return null;
    if (provider === "paypal") return { kind: "url", href: `https://paypal.me/${handle}` };
    if (provider === "venmo") return { kind: "url", href: `https://venmo.com/u/${handle}` };
    return null; // unknown provider — can't guess a domain
  }
  // Bare domain / path like "paypal.me/user" or "www.example.com/pay/xyz".
  return { kind: "url", href: `https://${s.replace(/^\/+/, "")}` };
}

function PaymentRow({ label, testId, href, provider }) {
  const norm = normalizePayLink(href, provider);
  if (!norm) return null;
  if (norm.kind === "email") {
    // PayPal-email fallback: no one-click link is possible, so give the
    // buyer a labelled copy-target + a fallback link to open paypal.com.
    return (
      <div className="p-3 bg-[#0A0A0A] border border-white/10 space-y-2" data-testid={testId}>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          Seller&apos;s PayPal Email
        </div>
        <div className="font-display font-bold text-base break-all" data-testid={`${testId}-email`}>
          {norm.email}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(norm.email);
                toast.success("Email copied");
              } catch {
                toast.error("Copy failed — long-press to select the email above.");
              }
            }}
            className="h57-btn-secondary text-xs flex-1 flex items-center justify-center gap-1"
            data-testid={`${testId}-copy`}
          >
            <Copy size={12} weight="bold" /> Copy Email
          </button>
          <a
            href="https://www.paypal.com/myaccount/transfer/homepage/pay"
            target="_blank"
            rel="noopener noreferrer"
            className="h57-btn-primary text-xs flex-1 flex items-center justify-center gap-1"
            data-testid={`${testId}-open`}
          >
            Open PayPal <ArrowUpRight size={12} weight="bold" />
          </a>
        </div>
        <p className="text-[10px] text-zinc-500 leading-snug">
          The seller provided an email instead of a `paypal.me/handle` link, so PayPal can&apos;t open a
          one-click payment. Copy the email, tap Open PayPal, then Send &amp; Request → paste.
        </p>
      </div>
    );
  }
  return (
    <a href={norm.href} target="_blank" rel="noopener noreferrer" data-testid={testId}
      className="h57-btn-primary text-sm flex items-center justify-between gap-2 w-full">
      <span>{label}</span>
      <ArrowUpRight size={14} weight="bold" />
    </a>
  );
}

function TxCard({ tx, meAsBuyer, onAction }) {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const { user: currentUser } = useAuth();

  const role = meAsBuyer ? "buyer" : "seller";
  const refresh = onAction;
  const availableCtas = cta[role][tx.status] || [];
  const status = STATUS_UI[tx.status] || { label: tx.status, cls: "" };

  // Buyer-side shipping-address completeness check — used to nudge the
  // buyer to add their address before marking paid, so the seller's
  // mark-paid email arrives with a complete ship-to.
  const buyerAddressIncomplete = meAsBuyer && !(
    currentUser?.shipping_name &&
    currentUser?.shipping_line1 &&
    currentUser?.shipping_city &&
    currentUser?.shipping_state &&
    currentUser?.shipping_zip
  );

  // Load seller payment info lazily when the tx is at a pay-relevant step.
  useEffect(() => {
    if (meAsBuyer && ["pending_buyer", "pending_shipment", "processing", "pending_feedback"].includes(tx.status)) {
      (async () => {
        try {
          const { data } = await api.get(`/transactions/${tx.id}`);
          setDetail(data);
        } catch { /* silent */ }
      })();
    }
  }, [tx.id, tx.status, meAsBuyer]);

  const call = async (path, body = null) => {
    setBusy(true);
    try {
      await api.post(`/transactions/${tx.id}${path}`, body || undefined);
      onAction();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  const [validateModalOpen, setValidateModalOpen] = useState(false);
  const [shippingInput, setShippingInput] = useState("");
  const [ccLinkInput, setCcLinkInput] = useState("");
  const [feesInput, setFeesInput] = useState("");
  const [feesNoteInput, setFeesNoteInput] = useState("");
  const [shippingHint, setShippingHint] = useState(null); // {suggested, sample_size}
  const openValidateModal = async () => {
    // Prefill from the listing's default shipping so sellers don't retype it.
    const listingHint = tx.listing_shipping_price;
    setShippingInput(listingHint != null ? String(listingHint) : "");
    setCcLinkInput("");
    setFeesInput("");
    setFeesNoteInput("");
    setValidateModalOpen(true);
    // Fetch the seller's rolling 30-day median as a soft suggestion.
    try {
      const { data } = await api.get("/transactions/seller/shipping-hint");
      setShippingHint(data);
      // Only auto-fill from history when the listing itself has no shipping set.
      if (listingHint == null && data?.suggested != null) {
        setShippingInput(String(data.suggested));
      }
    } catch { /* silent — hint is optional */ }
  };
  const doValidate = async () => {
    const raw = shippingInput === "" ? 0 : parseFloat(shippingInput);
    if (!isFinite(raw) || raw < 0) {
      toast.error("Enter a shipping charge of $0.00 or more.");
      return;
    }
    if (raw > 500) {
      toast.error("Shipping charge cannot exceed $500.");
      return;
    }
    const fees = feesInput === "" ? 0 : parseFloat(feesInput);
    if (feesInput !== "" && (!isFinite(fees) || fees < 0 || fees > 500)) {
      toast.error("Additional fees must be $0.00 - $500.00.");
      return;
    }
    // CC pay-link is required when the buyer chose credit_card.
    if (tx.payment_method === "credit_card") {
      const link = ccLinkInput.trim();
      if (!link) {
        toast.error("Paste your credit-card payment link for this buyer.");
        return;
      }
      if (!/^https?:\/\//i.test(link)) {
        toast.error("Payment link must start with http:// or https://");
        return;
      }
    }
    setBusy(true);
    try {
      await api.post(`/transactions/${tx.id}/validate`, {
        shipping_cost: raw,
        additional_fees: fees,
        additional_fees_note: feesNoteInput.trim() || null,
        cc_payment_link: tx.payment_method === "credit_card" ? ccLinkInput.trim() : undefined,
      });
      setValidateModalOpen(false);
      toast.success(`Sale validated — @${tx.buyer_username} notified for $${(tx.sale_price + raw + fees).toFixed(2)}.`);
      onAction();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };
  const doComplete = () => {
    if (!window.confirm(`Mark this sale complete now?\n\nThe sale will move to feedback — both you and @${tx.buyer_username} can then leave feedback. If you skip this step, the sale auto-completes 14 days after shipping.`)) return;
    call("/complete");
  };
  const doMarkPaid = () => {
    const total = tx.total_price ?? tx.sale_price;
    const warning = buyerAddressIncomplete
      ? "⚠ You have no complete shipping address on file. The seller will not know where to ship your card. It is strongly recommended you add your shipping address in your Account page BEFORE marking this PO paid.\n\n"
      : "";
    if (!window.confirm(
      warning +
      `Confirm you've paid @${tx.seller_username} the full amount ($${total.toFixed(2)}) via their listed payment method?\n\n` +
      `Only click if payment has actually been sent. This starts the seller's handling-time clock.`
    )) return;
    call("/mark-paid");
  };
  const doCancel = () => {
    const other = meAsBuyer ? tx.seller_username : tx.buyer_username;
    if (!window.confirm(
      `Cancel this Purchase Order?\n\n` +
      `The card will return to the browse board and @${other} will be notified. ` +
      `Cancel is only available BEFORE the buyer marks payment.`,
    )) return;
    call("/cancel");
  };

  const [shipModalOpen, setShipModalOpen] = useState(false);
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("");
  const doMarkShipped = async () => {
    setBusy(true);
    try {
      await api.post(`/transactions/${tx.id}/mark-shipped`, {
        tracking_number: tracking.trim() || null,
        carrier: carrier.trim() || null,
      });
      toast.success("Marked as shipped");
      setShipModalOpen(false);
      setTracking(""); setCarrier("");
      refresh();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  const doDecline = async () => {
    setBusy(true);
    try {
      await api.post(`/transactions/${tx.id}/decline`, { reason: reason.trim() || null });
      toast.success("PO declined");
      setDeclineOpen(false); setReason("");
      onAction();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };
  const doDispute = async () => {
    if (reason.trim().length < 3) { toast.error("Add a short reason (3+ chars)"); return; }
    setBusy(true);
    try {
      await api.post(`/transactions/${tx.id}/dispute`, { reason: reason.trim() });
      toast.success("Dispute opened — admin will review");
      setDisputeOpen(false); setReason("");
      onAction();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };
  const doFeedback = async () => {
    if (!stars) { toast.error("Pick a star rating"); return; }
    setBusy(true);
    try {
      await api.post(`/transactions/${tx.id}/feedback`, { stars, comment: comment.trim() || null });
      toast.success("Feedback submitted");
      setFeedbackOpen(false);
      onAction();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  const otherParty = meAsBuyer ? tx.seller_username : tx.buyer_username;

  return (
    <div className="h57-surface p-4" data-testid={`tx-card-${tx.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex gap-3">
          {tx.listing_front_image && (
            <img src={imgUrl(tx.listing_front_image, 240)} alt={tx.listing_title}
              className="w-16 h-20 object-cover bg-black/50 border border-white/10" />
          )}
          <div>
            <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest inline-flex items-center gap-1">
              {meAsBuyer ? "Purchase from" : "Sale to"} @{otherParty}
              <FoundingHat founding={meAsBuyer ? tx.seller_founding_member : tx.buyer_founding_member} size={11} />
            </div>
            <Link to={`/listing/${tx.listing_id}`}
              className="mt-1 block font-display font-black uppercase tracking-tight text-lg hover:text-[#FF5F1F]">
              {tx.listing_title || "(listing removed)"}
              <ArrowUpRight size={14} weight="bold" className="inline ml-1 -mt-1" />
            </Link>
            <div className="mt-1 text-xs text-zinc-500" data-testid={`tx-price-summary-${tx.id}`}>
              {tx.total_price != null && tx.shipping_cost != null ? (
                <>
                  <span className="text-white">${Number(tx.total_price).toFixed(2)} total</span>
                  <span className="text-zinc-600"> · ${Number(tx.sale_price || 0).toFixed(2)} card + ${Number(tx.shipping_cost).toFixed(2)} ship</span>
                  {tx.additional_fees > 0 && (
                    <span className="text-zinc-600"> + ${Number(tx.additional_fees).toFixed(2)} {tx.additional_fees_note || "fees"}</span>
                  )}
                </>
              ) : (
                <>${Number(tx.sale_price || 0).toFixed(2)}</>
              )}
              <span className="text-zinc-600"> · Opened {new Date(tx.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-1 items-end">
          <span className={`h57-chip ${status.cls}`} data-testid={`tx-status-${tx.id}`}>{status.label}</span>
          {tx.qualifying && <span className="h57-chip h57-chip-gold">Qualifying Sale</span>}
          {tx.expires_at && !["completed","declined","cancelled","expired","disputed"].includes(tx.status) && (
            <CountdownChip expiresAt={tx.expires_at} />
          )}
        </div>
      </div>

      {/* Buyer at pay step — shipping-address nudge. Rendered BEFORE the pay
          panel so the buyer sees it early and can fix their profile before
          they send payment. Non-blocking — the buyer can still mark paid,
          but the seller will get a "no address on file" prompt in that case. */}
      {meAsBuyer && tx.status === "pending_buyer" && buyerAddressIncomplete && (
        <div
          className="mt-4 p-3 border border-[#FFD700]/50 bg-[#FFD700]/10"
          data-testid={`tx-address-nudge-${tx.id}`}
        >
          <div className="flex items-start gap-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFD700] shrink-0 mt-0.5">⚠</div>
            <div className="flex-1">
              <div className="font-display font-bold uppercase text-xs tracking-widest text-[#FFD700]">
                Add your shipping address
              </div>
              <p className="mt-1 text-[11px] text-zinc-300 leading-relaxed">
                You don&apos;t have a complete shipping address saved in your profile.
                The seller will not know where to ship your card. Please add it before marking this PO paid.
              </p>
              <Link
                to="/account"
                data-testid={`tx-address-nudge-cta-${tx.id}`}
                className="mt-2 inline-block h57-btn-secondary text-[11px] py-1.5 px-3"
              >
                Add Shipping Address →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Buyer at pay step — show ONLY the buyer's chosen payment path. */}
      {meAsBuyer && tx.status === "pending_buyer" && detail?.seller_payment && (
        <div className="mt-4 p-3 border border-[#FFD700]/30 bg-[#FFD700]/5" data-testid={`tx-pay-panel-${tx.id}`}>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFD700]">→ Pay @{detail.seller_payment.username}</div>
          <p className="mt-1 text-xs text-zinc-400">
            Send the seller the <span className="text-white">${Number(tx.total_price ?? tx.sale_price ?? 0).toFixed(2)} total</span>
            {tx.shipping_cost != null && (
              <span className="text-zinc-500"> (${Number(tx.sale_price || 0).toFixed(2)} card + ${Number(tx.shipping_cost).toFixed(2)} shipping{tx.additional_fees > 0 ? ` + $${Number(tx.additional_fees).toFixed(2)} ${tx.additional_fees_note || "fees"}` : ""})</span>
            )} via the method you selected. Come back and click <span className="text-white">Mark Paid</span> once payment has been sent.
          </p>
          <div className="mt-3 space-y-2">
            {detail.seller_payment.method === "paypal" && (
              <PaymentRow label="Pay with PayPal" testId={`tx-pay-paypal-${tx.id}`} href={detail.seller_payment.paypal_link} provider="paypal" />
            )}
            {detail.seller_payment.method === "cashapp" && (
              <PaymentRow label="Cash App" testId={`tx-pay-cashapp-${tx.id}`}
                href={detail.seller_payment.cashapp_handle ? `https://cash.app/${detail.seller_payment.cashapp_handle.replace(/^\$/, "$")}` : null} provider="cashapp" />
            )}
            {detail.seller_payment.method === "venmo" && (
              <PaymentRow label="Venmo" testId={`tx-pay-venmo-${tx.id}`} href={detail.seller_payment.venmo_link} provider="venmo" />
            )}
            {detail.seller_payment.method === "credit_card" && (
              <>
                <PaymentRow label="Pay with Credit Card →" testId={`tx-pay-cc-${tx.id}`} href={detail.seller_payment.cc_payment_link} provider="cc" />
                {detail.seller_payment.credit_card_note && (
                  <div className="text-[11px] text-zinc-400 italic">Seller note: {detail.seller_payment.credit_card_note}</div>
                )}
              </>
            )}
            {detail.seller_payment.method === "other" && detail.seller_payment.other_payment_notes && (
              <div className="p-2 border border-white/10 text-sm text-zinc-300 whitespace-pre-wrap" data-testid={`tx-pay-other-${tx.id}`}>
                {detail.seller_payment.other_payment_notes}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Seller ship-to panel — shown once the buyer has marked paid.
          Snapshotted at mark-paid time so it stays immutable. */}
      {!meAsBuyer && tx.buyer_ship_to && ["pending_shipment", "processing", "pending_feedback", "completed"].includes(tx.status) && (
        <div className="mt-4 p-3 border border-[#007AFF]/30 bg-[#007AFF]/5" data-testid={`tx-ship-to-${tx.id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#007AFF]">→ Ship to @{tx.buyer_username}</div>
            {(() => {
              const addr = tx.buyer_ship_to || {};
              const complete = addr.name && addr.line1 && addr.city && addr.state && addr.zip;
              if (!complete) return null;
              const copyText = [
                addr.name,
                addr.line1,
                addr.line2,
                [addr.city, addr.state, addr.zip].filter(Boolean).join(", ").replace(/, (\S+)$/, " $1"),
                addr.country && !["US","USA","UNITED STATES"].includes((addr.country || "").toUpperCase()) ? addr.country : null,
                addr.phone ? `Phone: ${addr.phone}` : null,
              ].filter(Boolean).join("\n");
              return (
                <button
                  type="button"
                  className="h57-btn-secondary text-[10px] py-1 px-2"
                  data-testid={`tx-ship-to-copy-${tx.id}`}
                  onClick={() => {
                    navigator.clipboard.writeText(copyText).then(
                      () => toast.success("Address copied"),
                      () => toast.error("Copy failed"),
                    );
                  }}
                >
                  Copy
                </button>
              );
            })()}
          </div>
          {(() => {
            const addr = tx.buyer_ship_to || {};
            const complete = addr.name && addr.line1 && addr.city && addr.state && addr.zip;
            if (!complete) {
              return (
                <div className="mt-2 text-xs text-[#FFD700]">
                  ⚠ Buyer has not saved a complete shipping address in their profile. Please message them to confirm the ship-to before shipping.
                </div>
              );
            }
            return (
              <div className="mt-2 text-sm text-zinc-200 leading-relaxed whitespace-pre-line font-body">
                <div className="font-semibold text-white">{addr.name}</div>
                <div>{addr.line1}</div>
                {addr.line2 && <div>{addr.line2}</div>}
                <div>{[addr.city, addr.state].filter(Boolean).join(", ")}{addr.zip ? ` ${addr.zip}` : ""}</div>
                {addr.country && !["US","USA","UNITED STATES"].includes((addr.country || "").toUpperCase()) && (
                  <div>{addr.country}</div>
                )}
                {addr.phone && <div className="text-zinc-500 text-xs mt-1">Phone: {addr.phone}</div>}
              </div>
            );
          })()}
        </div>
      )}

      {/* Feedback rendered when it exists — both directions */}
      {(tx.buyer_feedback_stars || tx.seller_feedback_stars) && (
        <div className="mt-3 space-y-2" data-testid={`tx-feedback-${tx.id}`}>
          {tx.buyer_feedback_stars && (
            <div className="p-3 border border-white/10 bg-black/30">
              <div className="flex items-center gap-2">
                <StarPicker value={tx.buyer_feedback_stars} onChange={() => {}} size={14} />
                <span className="text-xs text-zinc-500">@{tx.buyer_username} → @{tx.seller_username}</span>
              </div>
              {tx.buyer_feedback_comment && <div className="mt-2 text-sm text-zinc-300">{tx.buyer_feedback_comment}</div>}
            </div>
          )}
          {tx.seller_feedback_stars && (
            <div className="p-3 border border-white/10 bg-black/30">
              <div className="flex items-center gap-2">
                <StarPicker value={tx.seller_feedback_stars} onChange={() => {}} size={14} />
                <span className="text-xs text-zinc-500">@{tx.seller_username} → @{tx.buyer_username}</span>
              </div>
              {tx.seller_feedback_comment && <div className="mt-2 text-sm text-zinc-300">{tx.seller_feedback_comment}</div>}
            </div>
          )}
        </div>
      )}

      {tx.status === "disputed" && tx.dispute_reason && (
        <div className="mt-3 p-3 border border-red-500/40 bg-red-500/5 text-sm text-red-300">
          <div className="font-mono text-[10px] uppercase tracking-widest text-red-400">Dispute · opened by {tx.disputed_by}</div>
          <div className="mt-1">{tx.dispute_reason}</div>
          {tx.admin_resolution && (
            <div className="mt-2 pt-2 border-t border-red-500/20 text-xs">
              <span className="font-mono uppercase tracking-widest text-red-400">Admin resolution: </span>{tx.admin_resolution}
            </div>
          )}
        </div>
      )}

      {tx.status === "declined" && tx.decline_reason && (
        <div className="mt-3 text-xs text-zinc-500">
          <span className="font-mono uppercase tracking-widest">Seller reason: </span>{tx.decline_reason}
        </div>
      )}

      {/* Ship deadline + tracking display — visible after seller validates */}
      {tx.ship_deadline_at && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-widest" data-testid={`tx-ship-info-${tx.id}`}>
          {tx.shipped_at ? (
            <span className="inline-flex items-center gap-1 border border-emerald-500 text-emerald-400 px-2 py-1">
              <Truck size={11} weight="bold" /> Shipped {new Date(tx.shipped_at).toLocaleDateString()}
            </span>
          ) : (
            <span
              className={`inline-flex items-center gap-1 border px-2 py-1 ${
                tx.strike_issued_ship_overdue
                  ? "border-red-500 text-red-400"
                  : "border-[#FFD700]/60 text-[#FFD700]"
              }`}
            >
              <ClockCountdown size={11} weight="bold" />
              {tx.strike_issued_ship_overdue ? "Ship overdue · strike issued" : `Ship by ${new Date(tx.ship_deadline_at).toLocaleDateString()}`}
            </span>
          )}
          {tx.tracking_number && (
            <span className="inline-flex items-center gap-1 border border-white/20 text-zinc-300 px-2 py-1" data-testid={`tx-tracking-${tx.id}`}>
              {tx.tracking_carrier ? `${tx.tracking_carrier} · ` : ""}Tracking {tx.tracking_number}
            </span>
          )}
        </div>
      )}

      {/* Actions */}
      {availableCtas.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {availableCtas.includes("validate") && (
            <button onClick={openValidateModal} disabled={busy} data-testid={`tx-validate-${tx.id}`}
              className="h57-btn-primary text-xs inline-flex items-center gap-1">
              <Check size={12} weight="bold" /> Validate PO
            </button>
          )}
          {availableCtas.includes("decline") && (
            <button onClick={() => setDeclineOpen(true)} data-testid={`tx-decline-${tx.id}`}
              className="text-xs inline-flex items-center gap-1 border border-red-500/50 text-red-400 hover:bg-red-500/10 font-mono uppercase tracking-widest py-2 px-3 transition-colors">
              <XCircle size={12} weight="bold" /> Decline
            </button>
          )}
          {availableCtas.includes("mark-paid") && (
            <button onClick={doMarkPaid} disabled={busy} data-testid={`tx-mark-paid-${tx.id}`}
              className="h57-btn-primary text-xs inline-flex items-center gap-1">
              <HandCoins size={12} weight="fill" /> I&apos;ve Paid
            </button>
          )}
          {availableCtas.includes("complete") && (
            <button onClick={doComplete} disabled={busy} data-testid={`tx-complete-${tx.id}`}
              className="h57-btn-primary text-xs inline-flex items-center gap-1">
              <Check size={12} weight="bold" /> Mark Complete
            </button>
          )}
          {availableCtas.includes("mark-shipped") && (
            <button
              onClick={() => setShipModalOpen(true)}
              disabled={busy}
              data-testid={`tx-mark-shipped-${tx.id}`}
              className={`text-xs inline-flex items-center gap-1 font-mono uppercase tracking-widest py-2 px-3 transition-colors ${tx.shipped_at ? "border border-emerald-500/50 text-emerald-400" : "border border-white/20 text-zinc-300 hover:text-white hover:border-white/40"}`}
            >
              <Truck size={12} weight="bold" /> {tx.shipped_at ? "Update Tracking" : "Mark Shipped"}
            </button>
          )}
          {availableCtas.includes("feedback") && !tx[`${role}_feedback_at`] && (
            <button onClick={() => setFeedbackOpen(true)} data-testid={`tx-feedback-open-${tx.id}`}
              className="h57-btn-primary text-xs inline-flex items-center gap-1">
              <Star size={12} weight="fill" /> Leave Feedback
            </button>
          )}
          {availableCtas.includes("cancel") && (
            <button onClick={doCancel} disabled={busy} data-testid={`tx-cancel-${tx.id}`}
              className="text-xs inline-flex items-center gap-1 border border-white/20 text-zinc-400 hover:text-white hover:border-white/40 font-mono uppercase tracking-widest py-2 px-3 transition-colors">
              <XCircle size={12} weight="bold" /> Cancel PO
            </button>
          )}
          {availableCtas.includes("dispute") && (
            <button onClick={() => setDisputeOpen(true)} data-testid={`tx-dispute-open-${tx.id}`}
              className="text-xs inline-flex items-center gap-1 border border-red-500/50 text-red-400 hover:bg-red-500/10 font-mono uppercase tracking-widest py-2 px-3 transition-colors">
              <WarningCircle size={12} weight="bold" /> Dispute
            </button>
          )}
        </div>
      )}

      {/* Decline modal */}
      {declineOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setDeclineOpen(false)}>
          <div className="max-w-md w-full bg-[#0A0A0A] border border-red-500/40 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-red-400">→ Decline PO</div>
            <div className="mt-1 font-display font-black uppercase text-lg">Send back to buyer?</div>
            <p className="mt-2 text-xs text-zinc-500">The listing will return to browse. Buyer will be notified.</p>
            <textarea className="h57-input mt-4 min-h-[80px]" data-testid={`tx-decline-reason-${tx.id}`}
              placeholder="Optional reason (out of stock, price change, etc.)" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setDeclineOpen(false)} className="h57-btn-secondary text-sm">Cancel</button>
              <button onClick={doDecline} disabled={busy} data-testid={`tx-decline-submit-${tx.id}`} className="h57-btn-primary text-sm !bg-red-600 !border-red-600 hover:!bg-red-500">
                {busy ? "…" : "Decline"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dispute modal */}
      {disputeOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setDisputeOpen(false)}>
          <div className="max-w-md w-full bg-[#0A0A0A] border border-red-500/40 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-red-400">→ Open Dispute</div>
            <div className="mt-1 font-display font-black uppercase text-lg">Report issue with this sale</div>
            <p className="mt-2 text-xs text-zinc-500">An admin will review your dispute.</p>
            <textarea className="h57-input mt-4 min-h-[100px]" data-testid={`tx-dispute-reason-${tx.id}`}
              placeholder="What went wrong?" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} />
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setDisputeOpen(false)} className="h57-btn-secondary text-sm">Cancel</button>
              <button onClick={doDispute} disabled={busy} data-testid={`tx-dispute-submit-${tx.id}`}
                className="text-sm inline-flex items-center gap-1 border border-red-500 bg-red-500/10 hover:bg-red-500/20 text-red-300 font-mono uppercase tracking-widest py-2 px-4">
                {busy ? "…" : "Open Dispute"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback modal — either party can leave feedback for the other */}
      {feedbackOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setFeedbackOpen(false)}>
          <div className="max-w-md w-full bg-[#0A0A0A] border border-white/15 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFD700]">→ Rate this sale</div>
            <div className="mt-1 font-display font-black uppercase text-lg">Feedback for @{otherParty}</div>
            <p className="mt-2 text-xs text-zinc-500">
              {meAsBuyer
                ? "4- and 5-star ratings count toward your seller's tier progression."
                : "Rate your experience with this buyer. The sale closes once both sides leave feedback."}
            </p>
            <div className="mt-4"><StarPicker value={stars} onChange={setStars} /></div>
            <textarea className="h57-input mt-4 min-h-[80px]" data-testid={`tx-feedback-comment-${tx.id}`}
              placeholder="Optional comment…" value={comment} onChange={(e) => setComment(e.target.value)} maxLength={1000} />
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setFeedbackOpen(false)} className="h57-btn-secondary text-sm">Cancel</button>
              <button onClick={doFeedback} disabled={busy} data-testid={`tx-feedback-submit-${tx.id}`} className="h57-btn-primary text-sm">
                {busy ? "…" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Validate modal — seller enters the shipping charge; card price is locked. */}
      {validateModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setValidateModalOpen(false)}>
          <div className="max-w-md w-full bg-[#0A0A0A] border border-white/15 p-6" onClick={(e) => e.stopPropagation()} data-testid={`tx-validate-modal-${tx.id}`}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">→ Validate PO</div>
            <div className="mt-1 font-display font-black uppercase text-lg">Confirm sale + shipping</div>
            <p className="mt-2 text-xs text-zinc-500">
              Enter the shipping charge the buyer will pay. The card price is locked from the listing and cannot be changed here.
            </p>

            {/* Buyer's chosen payment path — informational */}
            <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-zinc-400" data-testid={`tx-validate-method-label-${tx.id}`}>
              Buyer chose: <span className="text-white">{
                { paypal: "PayPal", cashapp: "Cash App", venmo: "Venmo",
                  credit_card: "Credit Card", other: "Other" }[tx.payment_method] || "—"
              }</span>
            </div>

            <div className="mt-4 p-3 border border-white/10 bg-black/30 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-500">Card price (locked)</span>
                <span className="font-mono text-white" data-testid={`tx-validate-card-price-${tx.id}`}>${(tx.sale_price || 0).toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <label className="text-sm text-zinc-500 shrink-0">Shipping charge</label>
                <div className="relative w-32">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 text-sm pointer-events-none">$</span>
                  <input
                    type="number" step="0.01" min="0" max="500"
                    value={shippingInput}
                    onChange={(e) => setShippingInput(e.target.value)}
                    data-testid={`tx-validate-shipping-${tx.id}`}
                    placeholder="0.00"
                    className="h57-input font-mono !pl-5 text-right"
                    autoFocus
                  />
                </div>
              </div>
              {shippingHint?.suggested != null && shippingHint.sample_size > 0 && (
                <button
                  type="button"
                  onClick={() => setShippingInput(String(shippingHint.suggested))}
                  data-testid={`tx-validate-hint-${tx.id}`}
                  className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-[#FFD700] transition-colors ml-auto block"
                >
                  Suggest ${Number(shippingHint.suggested).toFixed(2)} <span className="text-zinc-600">· median of last {shippingHint.sample_size}</span>
                </button>
              )}
              {tx.payment_method === "credit_card" && (
                <div className="pt-2 border-t border-white/10">
                  <label className="h57-label !mt-0">Credit-card Pay Link (required)</label>
                  <input
                    type="url"
                    value={ccLinkInput}
                    onChange={(e) => setCcLinkInput(e.target.value)}
                    data-testid={`tx-validate-cc-link-${tx.id}`}
                    placeholder="https://buy.stripe.com/… or PayPal invoice URL"
                    className="h57-input font-mono text-xs"
                    maxLength={500}
                  />
                  <p className="mt-1 text-[10px] text-zinc-500">
                    Paste a unique pay link for this buyer. They&apos;ll see it in their pay panel once you validate.
                  </p>
                </div>
              )}
              {/* Optional additional fee + label (e.g. CC surcharge, insurance) */}
              <div className="pt-2 border-t border-white/10 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <label className="text-sm text-zinc-500 shrink-0">Additional fees <span className="text-zinc-600">(optional)</span></label>
                  <div className="relative w-32">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 text-sm pointer-events-none">$</span>
                    <input
                      type="number" step="0.01" min="0" max="500"
                      value={feesInput}
                      onChange={(e) => setFeesInput(e.target.value)}
                      data-testid={`tx-validate-fees-${tx.id}`}
                      placeholder="0.00"
                      className="h57-input font-mono !pl-5 text-right"
                    />
                  </div>
                </div>
                {feesInput && parseFloat(feesInput) > 0 && (
                  <input
                    type="text"
                    value={feesNoteInput}
                    onChange={(e) => setFeesNoteInput(e.target.value)}
                    data-testid={`tx-validate-fees-note-${tx.id}`}
                    placeholder="What is this fee? e.g. '3.5% CC surcharge' (shown to buyer)"
                    className="h57-input text-xs"
                    maxLength={140}
                  />
                )}
              </div>
              <div className="border-t border-white/10 pt-2 flex items-center justify-between">
                <span className="font-display font-black uppercase text-sm text-[#FFD700]">Buyer pays</span>
                <span className="font-mono font-black text-lg text-[#FFD700]" data-testid={`tx-validate-total-${tx.id}`}>
                  ${(((tx.sale_price || 0) + (parseFloat(shippingInput) || 0) + (parseFloat(feesInput) || 0)) || 0).toFixed(2)}
                </span>
              </div>
            </div>

            <p className="mt-3 text-[10px] text-zinc-500 leading-relaxed">
              Once you validate, @{tx.buyer_username} is notified of the total and has 72 hours to pay.
              Your handling clock only starts when they mark the sale paid.
            </p>

            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setValidateModalOpen(false)} className="h57-btn-secondary text-sm">Cancel</button>
              <button onClick={doValidate} disabled={busy} data-testid={`tx-validate-submit-${tx.id}`} className="h57-btn-primary text-sm inline-flex items-center gap-2">
                <Check size={14} weight="bold" /> {busy ? "…" : "Validate Sale"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Mark Shipped modal */}
      {shipModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShipModalOpen(false)}>
          <div className="max-w-md w-full bg-[#0A0A0A] border border-white/15 p-6" onClick={(e) => e.stopPropagation()} data-testid={`tx-ship-modal-${tx.id}`}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">→ Mark shipped</div>
            <div className="mt-1 font-display font-black uppercase text-lg">Confirm shipment</div>
            <p className="mt-2 text-xs text-zinc-500">
              Ship deadline: <span className="text-zinc-300">{tx.ship_deadline_at ? new Date(tx.ship_deadline_at).toLocaleString() : "n/a"}</span>. Marking shipped protects you from an overdue-shipment strike even if the buyer hasn&apos;t confirmed receipt yet.
            </p>
            <label className="h57-label mt-4">Tracking Number <span className="normal-case tracking-normal text-zinc-500">(optional but strongly recommended)</span></label>
            <input
              className="h57-input font-mono"
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
              data-testid={`tx-ship-tracking-${tx.id}`}
              placeholder="e.g. 9400123456789012345678"
              maxLength={100}
            />
            <label className="h57-label mt-3">Carrier <span className="normal-case tracking-normal text-zinc-500">(optional)</span></label>
            <input
              className="h57-input"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              data-testid={`tx-ship-carrier-${tx.id}`}
              placeholder="USPS · UPS · FedEx"
              maxLength={60}
            />
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setShipModalOpen(false)} className="h57-btn-secondary text-sm">Cancel</button>
              <button onClick={doMarkShipped} disabled={busy} data-testid={`tx-ship-submit-${tx.id}`} className="h57-btn-primary text-sm">
                {busy ? "…" : "Mark Shipped"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Transactions() {
  const { user } = useAuth();
  const [tab, setTab] = useState("purchases");
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/transactions/mine");
      setTxs(data.transactions || []);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (!user) return null;

  const purchases = txs.filter((t) => t.buyer_id === user.id);
  const sales = txs.filter((t) => t.seller_id === user.id);
  const showing = tab === "purchases" ? purchases : sales;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-4 md:py-8">
      <div className="font-mono text-xs text-[#FF5F1F] tracking-widest uppercase">→ Purchase Orders</div>
      <h1 className="mt-2 font-display font-black uppercase text-3xl md:text-4xl tracking-tighter">Sales &amp; Purchases</h1>
      <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
        Buyer opens PO → seller validates → buyer pays externally + marks paid → seller ships within their handling window → sale enters processing → seller confirms complete (or auto-completes at 14 days) → both parties leave feedback.
        Hunt57 never touches your money. Miss a 72-hour deadline or your handling window and you receive a strike.
      </p>

      <div className="mt-6 flex gap-2 border-b border-white/10 pb-3">
        <button onClick={() => setTab("purchases")} data-testid="tx-tab-purchases"
          className={`h57-chip !py-2 !px-3 ${tab === "purchases" ? "!border-[#FF5F1F] !text-[#FF5F1F]" : ""}`}>
          <HandCoins size={12} weight="bold" className="inline mr-1" />
          Purchases <span className="ml-1 font-mono text-[10px] opacity-70">({purchases.length})</span>
        </button>
        <button onClick={() => setTab("sales")} data-testid="tx-tab-sales"
          className={`h57-chip !py-2 !px-3 ${tab === "sales" ? "!border-[#FF5F1F] !text-[#FF5F1F]" : ""}`}>
          Sales <span className="ml-1 font-mono text-[10px] opacity-70">({sales.length})</span>
        </button>
      </div>

      {loading ? (
        <div className="py-24 text-center font-mono text-xs uppercase tracking-widest text-zinc-500">Loading…</div>
      ) : showing.length === 0 ? (
        <div className="py-24 text-center" data-testid="tx-empty">
          <div className="font-display font-black uppercase text-xl text-zinc-500">
            No {tab} yet.
          </div>
          <p className="mt-2 text-sm text-zinc-600">
            {tab === "purchases"
              ? "Hit Purchase on a card to open your first PO."
              : "When a buyer opens a PO on one of your listings, it'll appear here."}
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {showing.map((tx) => (
            <TxCard key={tx.id} tx={tx} meAsBuyer={tab === "purchases"} onAction={load} />
          ))}
        </div>
      )}
    </div>
  );
}
